import moment from 'moment';

import { STATUS_CHANGE_REASONS } from '../util/status-change-reasons';

export const REJECTED_STATUS = 'REJECTED';
export const APPLIED_STATUS = 'APPLIED';
export const ARCHIVED_STATUS = 'ARCHIVED';
export const ACCEPTED_STATUS = 'ACCEPTED';
export const PHONE_INTERVIEWING_STATUS = 'PHONE_INTERVIEWING';
export const INTERVIEWING_STATUS = 'INTERVIEWING';
export const VALIDATING_STATUS = 'VALIDATING';
export const EMPLOYED_STATUS = 'EMPLOYED';
export const SAVED_STATUS = 'SAVED';

export const statusDisplay = (status: string | null, isCurrentEmployee?: boolean | null) => {
  switch (status) {
    case REJECTED_STATUS:
      return 'Rejected';
    case APPLIED_STATUS:
      return 'Applied';
    case ARCHIVED_STATUS:
      return 'Expired';
    case ACCEPTED_STATUS:
      return 'Interested';
    case PHONE_INTERVIEWING_STATUS:
      return 'Phone Interviewing';
    case INTERVIEWING_STATUS:
      return 'Interviewing';
    case VALIDATING_STATUS:
    case 'HIRED':
      // HIRED is no longer a valid status, but we want to be able to display historical
      // references to it
      return 'Onboarding';
    case EMPLOYED_STATUS:
      // If the current employee option was never passed in, or the current application doesn't have
      // an employee (aka, a null left join) then this should say started
      if (typeof isCurrentEmployee === 'undefined' || isCurrentEmployee === null) {
        return 'Started';
      }
      // Otherwise, it's defined as a boolean
      return isCurrentEmployee ? 'Started' : 'Former Employee';
    case SAVED_STATUS:
      return 'Saved';
    case null:
      return 'All';
    default:
      return 'Unknown';
  }
};

export const inProgressStatuses = (usesPhoneInterview: boolean) =>
  [
    ACCEPTED_STATUS,
    ...(usesPhoneInterview ? [PHONE_INTERVIEWING_STATUS] : []),
    INTERVIEWING_STATUS,
    VALIDATING_STATUS,
  ] as Array<ApplicationStatus>;

export const getOtherButtonStatuses = (usesPhoneInterview: boolean) =>
  [
    ACCEPTED_STATUS,
    ...(usesPhoneInterview ? [PHONE_INTERVIEWING_STATUS] : []),
    INTERVIEWING_STATUS,
    VALIDATING_STATUS,
    EMPLOYED_STATUS,
    SAVED_STATUS,
  ] as Array<ApplicationStatus>;

/**
 * Returns all the statuses IN ORDER OF HIRING FLOW. If usesPhoneInterview, we will add
 * PHONE_INTERVIEWING_STATUS.
 */
export const getAllHiringStatuses = (usesPhoneInterview: boolean): Array<ApplicationStatus> =>
  [
    APPLIED_STATUS,
    ACCEPTED_STATUS,
    ...(usesPhoneInterview ? [PHONE_INTERVIEWING_STATUS] : []),
    INTERVIEWING_STATUS,
    VALIDATING_STATUS,
    EMPLOYED_STATUS,
  ] as Array<ApplicationStatus>;

export const getAllStatuses = (usesPhoneInterview: boolean) =>
  getAllHiringStatuses(usesPhoneInterview).concat([REJECTED_STATUS, ARCHIVED_STATUS, SAVED_STATUS]);

export const allPossibleStatuses: Array<ApplicationStatus> = getAllStatuses(true);

// We don't load rejected or archived applicants by default because older customers will have a lot
// of them. Components that specifically deal with rejected applicants should load them separately
export const STATUSES_EXCLUDED_BY_DEFAULT: ApplicationStatus[] = [REJECTED_STATUS, ARCHIVED_STATUS];
export const STATUSES_TO_DISPLAY = allPossibleStatuses.filter(
  (s) => !STATUSES_EXCLUDED_BY_DEFAULT.includes(s),
);

/**
 * This is currently unused. It isn't correct because
 * it doesn't account for the phone interview stage being on or
 * off. We have to redo a big portion of the code to get the
 * 100 applicant and flow charts working again, so we'll update this
 * then.
 */
export const getPreviousStatus = (status: string) => {
  switch (status) {
    case ACCEPTED_STATUS:
      return APPLIED_STATUS;
    case INTERVIEWING_STATUS:
      return ACCEPTED_STATUS;
    case VALIDATING_STATUS:
      return INTERVIEWING_STATUS;
    case EMPLOYED_STATUS:
      return VALIDATING_STATUS;
    default:
      return undefined;
  }
};

interface StatusChangeStep {
  status: ApplicationStatus;
  text: string;
  reasonId?: number;
}
type StatusWorkflow = { [status in ApplicationStatus]: Array<StatusChangeStep> };
export const getStatusWorkflow = (
  usesPhoneInterview: boolean,
  applicant?: FullApplicant,
): StatusWorkflow => ({
  [REJECTED_STATUS]: [
    {
      status: SAVED_STATUS,
      text: 'Save',
    },
    {
      status: ACCEPTED_STATUS,
      text: 'Interested',
    },
  ],
  [APPLIED_STATUS]: [
    {
      status: SAVED_STATUS,
      text: 'Save',
    },
    {
      status: REJECTED_STATUS,
      text: 'Reject',
    },
    {
      status: ACCEPTED_STATUS,
      text: 'Interested',
    },
  ],
  [ARCHIVED_STATUS]: [
    {
      status: SAVED_STATUS,
      text: 'Save',
    },
    {
      status: REJECTED_STATUS,
      text: 'Reject',
    },
    {
      status: ACCEPTED_STATUS,
      text: 'Interested',
    },
  ],
  [ACCEPTED_STATUS]: [
    {
      status: SAVED_STATUS,
      text: 'Save',
    },
    {
      status: REJECTED_STATUS,
      text: 'Reject',
    },
    ...((usesPhoneInterview
      ? [
          {
            status: PHONE_INTERVIEWING_STATUS,
            text: 'Scheduled Phone Interview',
          },
        ]
      : [
          {
            status: INTERVIEWING_STATUS,
            text: 'Scheduled Interview',
          },
        ]) as Array<StatusChangeStep>),
  ],
  [PHONE_INTERVIEWING_STATUS]: [
    ...((applicant &&
    applicant.interviewStartAt &&
    moment.utc(applicant.interviewStartAt).isBefore(moment.utc())
      ? [
          {
            status: REJECTED_STATUS,
            text: "Didn't Show Up",
            reasonId: STATUS_CHANGE_REASONS.PHONE_INTERVIEWING_REJECTED_DIDNT_ANSWER,
          },
        ]
      : []) as Array<StatusChangeStep>),
    {
      status: REJECTED_STATUS,
      text: 'Rejected',
    },
    {
      status: PHONE_INTERVIEWING_STATUS,
      text: 'Rescheduled',
    },
    {
      status: INTERVIEWING_STATUS,
      text: 'Scheduled Interview',
    },
  ],
  [INTERVIEWING_STATUS]: [
    ...((applicant &&
    applicant.interviewStartAt &&
    moment.utc(applicant.interviewStartAt).isBefore(moment.utc())
      ? [
          {
            status: REJECTED_STATUS,
            text: "Didn't Show Up",
            reasonId: STATUS_CHANGE_REASONS.REJECTED_DIDNT_SHOW,
          },
        ]
      : []) as Array<StatusChangeStep>),
    {
      status: REJECTED_STATUS,
      text: 'Rejected',
    },
    {
      status: INTERVIEWING_STATUS,
      text: 'Rescheduled',
    },
    {
      status: VALIDATING_STATUS,
      text: 'Onboard',
    },
  ],
  [VALIDATING_STATUS]: [
    {
      status: REJECTED_STATUS,
      text: 'Reject',
    },
    {
      status: EMPLOYED_STATUS,
      text: 'Started',
    },
  ],
  [EMPLOYED_STATUS]: [],
  [SAVED_STATUS]: [
    {
      status: REJECTED_STATUS,
      text: 'Reject',
    },
    {
      status: ACCEPTED_STATUS,
      text: 'Interested',
    },
  ],
});

/**
 * This is a type guard that can take any input and verify that it is an ApplicationStatus. If you
 * pass a variable into this function in an if statement, the variable will be verified to being an
 * ApplicationStatus type inside the if block.
 * @param status variable to type check
 * @returns whether or not the input is in the ApplicationStatus type
 */
export const isApplicationStatus = (status: any): status is ApplicationStatus => {
  if (typeof status !== 'string') {
    return false;
  }

  switch (status) {
    case REJECTED_STATUS:
    case APPLIED_STATUS:
    case ARCHIVED_STATUS:
    case ACCEPTED_STATUS:
    case PHONE_INTERVIEWING_STATUS:
    case INTERVIEWING_STATUS:
    case VALIDATING_STATUS:
    case EMPLOYED_STATUS:
    case SAVED_STATUS:
      return true;
    default:
      return false;
  }
};
